import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { countTrackedChanges, isTrackingOn, readChangeTrackingMode } from "@/office/word/review";

export const lektionAnnehmen: Lesson = {
  id: "annehmen-und-ablehnen",
  title: "Änderungen annehmen und ablehnen",
  summary: "Aus einem überarbeiteten Entwurf eine endgültige Fassung machen.",
  durationMinutes: 8,
  learningGoals: [
    "Einzelne Änderungen gezielt annehmen oder ablehnen",
    "Sich mit „Weiter“ durch alle Änderungen arbeiten",
    "Die Sammelfunktionen bewusst einsetzen",
  ],
  steps: [
    {
      id: "einzeln-entscheiden",
      title: "Änderung für Änderung entscheiden",
      explanation: [
        "Kommt ein überarbeitetes Dokument zurück, liegt die Entscheidung wieder bei dir. " +
          "Auf der Registerkarte „Überprüfen“ in der Gruppe „Änderungen“ stehen dafür " +
          "„Annehmen“ und „Ablehnen“, dazu „Zurück“ und „Weiter“.",
        "Der saubere Weg: Cursor an den Anfang des Dokuments, dann mit „Weiter“ von " +
          "Änderung zu Änderung springen und jede einzeln entscheiden. Word setzt den " +
          "Cursor automatisch auf die nächste Stelle.",
        "In den Aufklappmenüs beider Schaltflächen liegt außerdem „Alle Änderungen " +
          "annehmen“ beziehungsweise ablehnen. Das spart Zeit – nimmt dir aber auch die " +
          "Kontrolle. Nutze es nur, wenn du die Überarbeitung bereits vollständig " +
          "gelesen hast.",
      ],
      keyPoints: [
        "Überprüfen → Gruppe „Änderungen“ → Annehmen / Ablehnen / Weiter.",
        "„Alle annehmen“ nur nach vollständiger Durchsicht.",
        "Auch Formatierungsänderungen werden aufgezeichnet und wollen entschieden werden.",
      ],
      task: {
        instruction:
          "Arbeite alle nachverfolgten Änderungen in deinem Dokument ab, bis keine mehr " +
          "offen ist – du kannst sie annehmen oder ablehnen.",
        hint: "Cursor an den Dokumentanfang setzen und dich mit „Weiter“ durcharbeiten.",
        check: async () => {
          const offen = await countTrackedChanges();
          if (offen === null) {
            return unsupported(
              "Das Auslesen einzelner nachverfolgter Änderungen braucht eine neuere " +
                "Word-Version, als hier zur Verfügung steht.",
              "Kontrolliere selbst über „Weiter“ auf der Registerkarte „Überprüfen“, ob noch Änderungen offen sind, und markiere den Schritt dann als erledigt.",
            );
          }

          return fromCriteria(
            [criterion(`Keine offenen Änderungen mehr (gefunden: ${offen})`, offen === 0)],
            {
              success: "Alle Änderungen sind entschieden – das Dokument ist bereinigt.",
              failure: `Es sind noch ${offen} Änderungen offen.`,
              hint: "Vergiss die Kopf- und Fußzeilen nicht, dort können ebenfalls Änderungen liegen.",
            },
          );
        },
      },
    },
    {
      id: "nachverfolgung-beenden",
      title: "Die Nachverfolgung beenden",
      explanation: [
        "Ist die Überarbeitung abgeschlossen, gehört die Nachverfolgung wieder " +
          "ausgeschaltet. Sonst zeichnet Word auch alle späteren Korrekturen auf – und " +
          "das nächste Mal wundert sich jemand über bunte Markierungen in einem " +
          "vermeintlich fertigen Dokument.",
        "Reihenfolge merken: erst alle Änderungen entscheiden, dann die Nachverfolgung " +
          "ausschalten. Andersherum bleiben die alten Änderungen weiterhin im Dokument " +
          "gespeichert, obwohl der Schalter aus ist.",
      ],
      keyPoints: [
        "Erst entscheiden, dann ausschalten.",
        "Ausschalten allein entfernt keine bereits aufgezeichneten Änderungen.",
        "Strg + Umschalt + V schaltet die Nachverfolgung wieder ab.",
      ],
      task: {
        instruction:
          "Schalte die Änderungsnachverfolgung wieder aus – nachdem alle Änderungen " +
          "entschieden sind.",
        hint: "Überprüfen → Änderungen nachverfolgen. Die Hervorhebung der Schaltfläche verschwindet.",
        check: async () => {
          const modus = await readChangeTrackingMode();
          if (modus === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in den Zustand der Änderungsnachverfolgung " +
                "nicht zur Verfügung.",
              "Prüfe die Schaltfläche selbst und markiere den Schritt dann als erledigt.",
            );
          }

          const offen = await countTrackedChanges();
          const kriterien = [
            criterion(`Die Nachverfolgung ist ausgeschaltet (Zustand: ${modus})`, !isTrackingOn(modus)),
          ];
          if (offen !== null) {
            kriterien.push(
              criterion(`Keine offenen Änderungen mehr (gefunden: ${offen})`, offen === 0),
            );
          }

          return fromCriteria(kriterien, {
            success: "Sauber abgeschlossen – das Dokument ist bereit zur Weitergabe.",
            failure: "Da ist noch etwas offen:",
            hint: "Denk an die Reihenfolge: zuerst alle Änderungen entscheiden, danach ausschalten.",
          });
        },
      },
    },
  ],
};
